// Port of SuiteModel/Fountain.swift: screenplay fragments store Fountain (fountain.io). Ranges are UTF-16, as in the
// Swift (NSString) and in JS; line tests that Swift makes by Character use the grapheme helpers.
import type { FountainApi, FountainElement, FountainLine, Inline, InlineStyle } from './contract.ts';
import {
  charIsLetter, charIsNumber, charIsWhitespace, firstGraphemes, graphemes, hasPrefix, hasSuffix, indexOf,
  splitLines, trimCFWhitespace,
} from './unicode.ts';

/** `Fountain.Element`: the contract's elements plus the two that never print. */
export type ClassifiedElement = FountainElement | 'boneyard' | 'blank';

/** The Swift raw values (`Fountain.Element.rawValue`), for fixtures. */
export const ELEMENT_RAW: Record<ClassifiedElement, string> = {
  sceneHeading: 'scene-heading', action: 'action', character: 'character', parenthetical: 'parenthetical',
  dialogue: 'dialogue', transition: 'transition', centered: 'centered', lyric: 'lyric', pageBreak: 'page-break',
  section: 'section', synopsis: 'synopsis', note: 'note', boneyard: 'boneyard', titlePage: 'title-page', blank: 'blank',
};

/** `Fountain.Line`: one classified line. */
export interface ClassifiedLine {
  element: ClassifiedElement;
  /** The whole line, without its newline (UTF-16). */
  location: number;
  length: number;
  /** Syntax before the content that isn't printed: a forcing character, `#`s, `=`, `~`, a title key. */
  leadingMarker: number;
  /** Syntax after the content: `<` of centred text, `^` of dual dialogue, ` #12#` scene numbers. */
  trailingMarker: number;
  isForced: boolean;
  isDual: boolean;
  sceneNumber?: string;
}

/** What prints: the line without its markers. */
export function contentRange(line: ClassifiedLine): [number, number] {
  return [line.location + line.leadingMarker, Math.max(0, line.length - line.leadingMarker - line.trailingMarker)];
}

const NEWLINE = 10;

/** `lineRanges`: lines starting at `start` up to and including one that starts at `end`. */
function lineRanges(text: string, start: number, end: number): [number, number][] {
  const ranges: [number, number][] = [];
  let lineStart = start;
  const length = text.length;
  while (lineStart <= end && lineStart <= length) {
    let lineEnd = lineStart;
    while (lineEnd < length && text.charCodeAt(lineEnd) !== NEWLINE) lineEnd++;
    ranges.push([lineStart, lineEnd - lineStart]);
    if (lineEnd >= length) break;
    lineStart = lineEnd + 1;
  }
  return ranges;
}

const TITLE_KEYS = new Set(['title', 'credit', 'author', 'authors', 'source', 'draft date', 'date', 'contact', 'copyright', 'notes', 'revision']);

/** `titleKey`: the `Key:` prefix (with any spaces after it) of a title-page line; "" for an indented continuation. */
export function titleKey(line: string): string | null {
  if (hasPrefix(line, '   ') || hasPrefix(line, '\t')) return '';
  const colon = indexOf(line, ':');
  if (colon < 0) return null;
  const key = trimCFWhitespace(line.slice(0, colon)).toLowerCase();
  if (!TITLE_KEYS.has(key)) return null;
  let end = colon + 1;
  while (end < line.length && line[end] === ' ' && hasPrefix(line.slice(end), ' ')) end++;
  return line.slice(0, end);
}

/** `leadingRun`: a run of `c` Characters, and one space after it. */
function leadingRun(line: string, c: string): number {
  const chars = graphemes(line);
  let n = 0;
  while (n < chars.length && chars[n] === c) n++;
  if (chars[n] === ' ') n += 1;
  return n;
}

/** `leadingLength(through:)`: up to the first `c` and the spaces after it, in UTF-16. */
function leadingLength(line: string, c: string): number {
  const chars = graphemes(line);
  const i = chars.indexOf(c);
  if (i < 0) return 0;
  let end = i + 1;
  while (end < chars.length && chars[end] === ' ') end++;
  return chars.slice(0, end).join('').length;
}

/** `trailingLength(from:)`: from the last `c`, and the spaces before it, to the end, in UTF-16. */
function trailingLength(line: string, c: string): number {
  const chars = graphemes(line);
  const i = chars.lastIndexOf(c);
  if (i < 0) return 0;
  let start = i;
  while (start > 0 && chars[start - 1] === ' ') start--;
  return chars.slice(start).join('').length;
}

const SCENE_HEADING = /^(?:INT\.?\/EXT|INT|EXT|EST|I\/E)[. ]/iu;

/** `isSceneHeading` (NSRegularExpression, case-insensitive). */
export function isSceneHeading(trimmed: string): boolean {
  return SCENE_HEADING.test(trimmed);
}

const containsLetter = (s: string) => graphemes(s).some(charIsLetter);

/** `isUppercase`: has a letter and uppercasing changes nothing. */
function isUppercase(s: string): boolean {
  return containsLetter(s) && s === s.toUpperCase();
}

/** `isCharacterCue`: an all-capitals name; an extension in parentheses (V.O., cont'd) may be any case. */
export function isCharacterCue(trimmed: string): boolean {
  const chars = graphemes(trimmed);
  const paren = chars.indexOf('(');
  let name = trimCFWhitespace(paren >= 0 ? chars.slice(0, paren).join('') : trimmed);
  if (hasSuffix(name, '^')) name = trimCFWhitespace(name.slice(0, -1));
  if (!containsLetter(name) || name !== name.toUpperCase()) return false;
  return !hasSuffix(trimmed, 'TO:');
}

// ICU's `\s` is [\t\n\f\r\p{Z}], and `$` also matches before a final line terminator.
const SCENE_NUMBER = /[\t\n\f\r\p{Z}]*#([A-Za-z0-9.\-]+)#[\t\n\f\r\p{Z}]*(?=(?:\r\n|[\n\r\u0085\u2028\u2029])?$)/u;

function sceneNumber(line: string, heading: ClassifiedLine) {
  const m = SCENE_NUMBER.exec(line);
  if (!m) return;
  heading.sceneNumber = m[1];
  heading.trailingMarker = m[0].length;
}

function dual(line: string, cue: ClassifiedLine) {
  let end = line.length;
  while (end > 0 && line.charCodeAt(end - 1) === 32) end--;
  if (end === 0 || line[end - 1] !== '^') return;
  let start = end - 1;
  while (start > 0 && line.charCodeAt(start - 1) === 32) start--;
  cue.isDual = true;
  cue.trailingMarker = line.length - start;
}

/** `Fountain.lines(_:range:limit:atDocumentStart:editing:)`: classifies the lines starting in the range, which must
 *  begin a line after a blank line (or the text's start); lines up to `limit` are read ahead for context. */
export function classify(text: string, options: { editing?: boolean; range?: [number, number]; limit?: number; atDocumentStart?: boolean } = {}): ClassifiedLine[] {
  const editing = options.editing ?? false;
  const [from, length] = options.range ?? [0, text.length];
  const limit = options.limit ?? text.length;
  const atDocumentStart = options.atDocumentStart ?? true;
  const ranges = lineRanges(text, from, from + length);
  const ownCount = ranges.length;
  // Two lines of context after the range, within the limit.
  if (ranges.length > 0) {
    const last = ranges[ranges.length - 1];
    let next = last[0] + last[1] + 1;
    for (let k = 0; k < 2 && next <= limit; k++) {
      let end = next;
      while (end < limit && text.charCodeAt(end) !== NEWLINE) end++;
      ranges.push([next, end - next]);
      next = end + 1;
    }
  }
  const strings = ranges.map(([loc, len]) => text.slice(loc, loc + len));
  const isBlank = (i: number) => i >= strings.length || graphemes(strings[i]).every(charIsWhitespace);
  const exists = (i: number) => i < strings.length;

  const result: ClassifiedLine[] = [];
  let inDialogue = false;
  let inBoneyard = false;
  /** The empty line after a cue, waiting for its dialogue (editing only). */
  let awaiting: number | null = null;
  let i = 0;
  const make = (element: ClassifiedElement, r: [number, number], leading = 0, trailing = 0, forced = false): ClassifiedLine =>
    ({ element, location: r[0], length: r[1], leadingMarker: leading, trailingMarker: trailing, isForced: forced, isDual: false });

  // A title page: `Key: value` lines at the very start, up to the first blank line.
  if (atDocumentStart && strings.length > 0) {
    const key = titleKey(strings[0]);
    if (key !== null && key !== '') {
      while (i < ownCount && !isBlank(i)) {
        const k = titleKey(strings[i]);
        result.push(make('titlePage', ranges[i], k === null ? 0 : k.length));
        i += 1;
      }
    }
  }

  for (; i < ownCount; i++) {
    const line = strings[i];
    const r = ranges[i];
    const trimmed = trimCFWhitespace(line);
    const previousBlank = i === 0 || isBlank(i - 1);
    const add = (element: ClassifiedElement, leading = 0, trailing = 0, forced = false) => result.push(make(element, r, leading, trailing, forced));

    if (inBoneyard) {
      add('boneyard');
      if (indexOf(line, '*/') >= 0) inBoneyard = false;
      continue;
    }
    if (hasPrefix(trimmed, '/*')) {
      add('boneyard');
      if (indexOf(graphemes(trimmed).slice(2).join(''), '*/') < 0) inBoneyard = true;
      inDialogue = false;
      continue;
    }
    if (trimmed === '') {
      // Two spaces keep a speech going across what looks like a blank line.
      if (inDialogue && (line === '  ' || i === awaiting)) { add('dialogue'); continue; }
      inDialogue = false;
      add('blank');
      continue;
    }
    if (inDialogue) {
      // A sung line in a speech is a lyric, as Fountain has it; the speech goes on after it.
      if (hasPrefix(line, '~')) { add('lyric', 1, 0, true); continue; }
      if (hasPrefix(trimmed, '(') && hasSuffix(trimmed, ')')) add('parenthetical'); else add('dialogue');
      continue;
    }
    const [first, second] = firstGraphemes(line, 2);
    const trimmedChars = graphemes(trimmed);
    if (trimmedChars.length >= 3 && trimmedChars.every((c) => c === '=')) { add('pageBreak'); continue; }
    if (first === '#') { add('section', leadingRun(line, '#')); continue; }
    if (first === '=') { add('synopsis', leadingRun(line, '=')); continue; }
    if (hasPrefix(trimmed, '[[') && hasSuffix(trimmed, ']]') && indexOf(trimmedChars.slice(2).join(''), '[[') < 0) { add('note'); continue; }
    if (first === '!') { add('action', 1, 0, true); continue; }
    if (first === '~') { add('lyric', 1, 0, true); continue; }
    if (previousBlank && first === '.' && second !== undefined && (charIsLetter(second) || charIsNumber(second))) {
      const heading = make('sceneHeading', r, 1, 0, true);
      sceneNumber(line, heading);
      result.push(heading);
      continue;
    }
    if (first === '>') {
      if (hasSuffix(trimmed, '<') && trimmedChars.length > 1) add('centered', leadingLength(line, '>'), trailingLength(line, '<'));
      else add('transition', 1, 0, true);
      continue;
    }
    if (first === '@') {
      if (editing && exists(i + 1) && strings[i + 1] === '') awaiting = i + 1;
      const cue = make('character', r, 1, 0, true);
      dual(line, cue);
      result.push(cue);
      inDialogue = true;
      continue;
    }
    if (previousBlank && isSceneHeading(trimmed)) {
      const heading = make('sceneHeading', r);
      sceneNumber(line, heading);
      result.push(heading);
      continue;
    }
    if (previousBlank && isUppercase(trimmed) && hasSuffix(trimmed, 'TO:') && isBlank(i + 1)) { add('transition'); continue; }
    if (previousBlank && isCharacterCue(trimmed)) {
      // Strictly, a cue needs dialogue on the next line. While writing, the empty line that Return made for the
      // dialogue counts, when nothing follows it.
      const dialogueFollows = exists(i + 1) && !isBlank(i + 1);
      const awaitingDialogue = editing && exists(i + 1) && strings[i + 1] === '' && isBlank(i + 2);
      if (dialogueFollows || awaitingDialogue) {
        if (awaitingDialogue) awaiting = i + 1;
        const cue = make('character', r);
        dual(line, cue);
        result.push(cue);
        inDialogue = true;
        continue;
      }
    }
    add('action');
  }
  return result;
}

// MARK: Inline

export type SpanKind = 'marker' | 'italic' | 'bold' | 'underline' | 'note';
export interface Span { kind: SpanKind; location: number; length: number }

const STAR = 42, UNDERSCORE = 95, BACKSLASH = 92, OPEN = 91, CLOSE = 93, SLASH = 47, SPACE = 32;

/** `inlineSpans`: emphasis, markers and notes within a line. */
export function inlineSpans(text: string, location: number, length: number): Span[] {
  const spans: Span[] = [];
  type Open = { start: number; marker: number } | null;
  let italic: Open = null, bold: Open = null, underline: Open = null;
  const end = location + length;
  let i = location;
  const at = (j: number) => text.charCodeAt(j);
  const close = (open: Open, kind: SpanKind, loc: number) => {
    if (open && loc > open.start) spans.push({ kind, location: open.start, length: loc - open.start });
  };
  const find = (a: number, b: number, from: number): number | null => {
    for (let j = from; j + 1 < end; j++) if (at(j) === a && at(j + 1) === b) return j;
    return null;
  };
  while (i < end) {
    const c = at(i);
    if (c === BACKSLASH && i + 1 < end) {
      spans.push({ kind: 'marker', location: i, length: 1 });
      i += 2;
      continue;
    }
    if (c === OPEN && i + 1 < end && at(i + 1) === OPEN) {
      const j = find(CLOSE, CLOSE, i + 2);
      if (j !== null) { spans.push({ kind: 'note', location: i, length: j + 2 - i }); i = j + 2; continue; }
    }
    if (c === SLASH && i + 1 < end && at(i + 1) === STAR) {
      const j = find(STAR, SLASH, i + 2);
      if (j !== null) { spans.push({ kind: 'note', location: i, length: j + 2 - i }); i = j + 2; continue; }
    }
    if (c === STAR) {
      let n = 0;
      while (i + n < end && at(i + n) === STAR) n++;
      const after = i + n;
      // A lone star with spaces either side is just a star.
      const spaced = n === 1 && (i === location || at(i - 1) === SPACE) && (after >= end || at(after) === SPACE);
      if (!spaced) {
        spans.push({ kind: 'marker', location: i, length: n });
        const marker = spans.length - 1;
        if (n % 2 === 1) { if (italic) { close(italic, 'italic', i); italic = null; } else italic = { start: after, marker }; }
        if (n >= 2) { if (bold) { close(bold, 'bold', i); bold = null; } else bold = { start: after, marker }; }
      }
      i = after;
      continue;
    }
    if (c === UNDERSCORE) {
      spans.push({ kind: 'marker', location: i, length: 1 });
      if (underline) { close(underline, 'underline', i); underline = null; } else underline = { start: i + 1, marker: spans.length - 1 };
      i += 1;
      continue;
    }
    i += 1;
  }
  // Emphasis never closed isn't emphasis: its opening marker is ordinary text after all.
  const orphans = new Set<number>();
  const u = underline as Open, o = italic as Open, b = bold as Open;
  if (u) orphans.add(u.marker);
  if (o && b && o.marker === b.marker) orphans.add(o.marker);
  else {
    if (o && spans[o.marker].length === 1) orphans.add(o.marker);
    if (b && spans[b.marker].length === 2) orphans.add(b.marker);
  }
  return spans.filter((_, n) => !orphans.has(n));
}

/** `Fountain.Run`: a printed stretch of text with one style. */
export interface Run { text: string; italic: boolean; bold: boolean; underline: boolean }

/** `runs`: the printed runs of a range: markers, notes and escapes removed, emphasis applied. */
export function runs(text: string, location: number, length: number): Run[] {
  const end = location + length;
  const size = Math.max(0, length);
  const hidden = new Uint8Array(size), italic = new Uint8Array(size), bold = new Uint8Array(size), underline = new Uint8Array(size);
  for (const span of inlineSpans(text, location, length)) {
    const target = span.kind === 'marker' || span.kind === 'note' ? hidden : span.kind === 'italic' ? italic : span.kind === 'bold' ? bold : underline;
    for (let j = span.location; j < span.location + span.length; j++) if (j >= location && j < end) target[j - location] = 1;
  }
  const out: Run[] = [];
  let i = location;
  const same = (j: number, k: number) => italic[j] === italic[k] && bold[j] === bold[k] && underline[j] === underline[k];
  while (i < end) {
    const x = i - location;
    if (hidden[x]) { i++; continue; }
    let j = i + 1;
    while (j < end && !hidden[j - location] && same(j - location, x)) j++;
    const piece = text.slice(i, j);
    const last = out[out.length - 1];
    if (last && last.italic === !!italic[x] && last.bold === !!bold[x] && last.underline === !!underline[x]) last.text += piece;
    else out.push({ text: piece, italic: !!italic[x], bold: !!bold[x], underline: !!underline[x] });
    i = j;
  }
  return out;
}

// MARK: The printed script

/** `Fountain.Paragraph`: a printed element, with its hard line breaks. */
export interface Paragraph {
  element: ClassifiedElement;
  /** One entry per line as written. */
  lines: Run[][];
  sceneNumber?: string;
  isDual: boolean;
}

export const paragraphText = (p: Paragraph) => p.lines.map((l) => l.map((r) => r.text).join('')).join('\n');

/** `trimmed`: spaces and tabs off both ends of a range. */
function trimmedRange(text: string, [location, length]: [number, number]): [number, number] {
  let start = location, end = location + length;
  const blank = (j: number) => text.charCodeAt(j) === 32 || text.charCodeAt(j) === 9;
  while (start < end && blank(start)) start++;
  while (end > start && blank(end - 1)) end--;
  return [start, end - start];
}

const INVISIBLE = new Set<ClassifiedElement>(['section', 'synopsis', 'note', 'boneyard', 'titlePage']);

/** Walks the printed script as `paragraphs` does, reporting invisible lines too (for `lines`). */
function walk(source: string, visit: {
  printed(element: ClassifiedElement, runs: Run[], line: ClassifiedLine, continues: boolean): void;
  invisible?(line: ClassifiedLine, text: string): void;
}) {
  const text = source.replaceAll('\r\n', '\n');
  let previous: ClassifiedElement = 'blank';
  for (const line of classify(text)) {
    let content = contentRange(line);
    const element = line.element;
    if (INVISIBLE.has(element)) { visit.invisible?.(line, text); continue; }   // doesn't break a paragraph
    if (element === 'blank') { previous = 'blank'; continue; }
    if (element === 'pageBreak') {
      visit.printed('pageBreak', [], line, false);
      previous = 'pageBreak';
      continue;
    }
    if (element !== 'action') content = trimmedRange(text, content);   // action keeps the writer's indentation
    let rs = runs(text, content[0], content[1]);
    if (element === 'sceneHeading' || element === 'character' || element === 'transition') rs = rs.map((r) => ({ ...r, text: r.text.toUpperCase() }));
    // A line of nothing but a note prints nothing (an empty dialogue line keeps a speech's gap).
    if (element !== 'dialogue' && trimCFWhitespace(rs.map((r) => r.text).join('')) === '') continue;
    const continues = element === previous && (element === 'action' || element === 'dialogue' || element === 'lyric');
    visit.printed(element, rs, line, continues);
    previous = element;
  }
}

/** `Fountain.paragraphs`: the elements that print, in order; consecutive action or dialogue lines become one paragraph. */
export function paragraphs(source: string): Paragraph[] {
  const result: Paragraph[] = [];
  walk(source, {
    printed(element, rs, line, continues) {
      if (element === 'pageBreak') { result.push({ element, lines: [], isDual: false }); return; }
      if (continues && result.length > 0) result[result.length - 1].lines.push(rs);
      else result.push({ element, lines: [rs], ...(line.sceneNumber !== undefined ? { sceneNumber: line.sceneNumber } : {}), isDual: line.isDual });
    },
  });
  return result;
}

/** `Fountain.titlePage`: the `Key: value` pairs, keys lowercased; multi-line values joined with newlines. */
export function titlePage(source: string): { key: string; value: string }[] {
  const pairs: { key: string; value: string }[] = [];
  for (const s of splitLines(source.replaceAll('\r\n', '\n'))) {
    if (trimCFWhitespace(s) === '') break;
    const key = titleKey(s);
    if (key === null) break;
    if (key === '') {
      if (pairs.length === 0) break;
      const more = trimCFWhitespace(s);
      const last = pairs[pairs.length - 1];
      last.value += last.value === '' ? more : '\n' + more;
    } else {
      const name = trimCFWhitespace(key.split(':').find((p) => p !== '') ?? '').toLowerCase();
      pairs.push({ key: name, value: trimCFWhitespace(s.slice(key.length)) });
    }
  }
  return pairs;
}

/** `Fountain.plainText`: words in the printed text (dialogue, action, headings), as the word count sees a screenplay. */
export function plainText(source: string): string {
  return paragraphs(source).map(paragraphText).join('\n\n');
}

// MARK: For reading (the contract's `lines`)

/** A `FountainLine` with what a reader needs to lay it out: lines of one paragraph share `paragraph`. */
export interface FountainLineDetail extends FountainLine {
  /** Lines with the same number are one printed paragraph (action or a speech's dialogue lines, run together). */
  paragraph: number;
  sceneNumber?: string;
}

const styleOf = (r: Run): InlineStyle => ({ italic: r.italic, bold: r.bold, smallCaps: false, underline: r.underline, highlight: null, color: null });
const inlinesOf = (rs: Run[]): Inline[] => rs.filter((r) => r.text !== '').map((r) => ({ type: 'text' as const, text: r.text, style: styleOf(r) }));
const plainInlines = (text: string): Inline[] => (text === '' ? [] : [{ type: 'text', text, style: styleOf({ text, italic: false, bold: false, underline: false }) }]);

/** Every line a reader shows, in order: printed lines as `paragraphs` prints them, plus the title page (values),
 *  sections, synopses and whole-line notes (their text, for the reader to show or hide). The boneyard and inline
 *  notes are left out. A dual cue and the speech under it are marked `dual`. */
export function lines(source: string): FountainLineDetail[] {
  const out: FountainLineDetail[] = [];
  let paragraph = -1;
  let dualSpeech = false;
  walk(source, {
    printed(element, rs, line, continues) {
      if (!continues) paragraph++;
      if (element === 'character') dualSpeech = line.isDual;
      else if (element !== 'parenthetical' && element !== 'dialogue') dualSpeech = false;
      const printed = element as FountainElement;
      out.push({
        element: printed, inlines: inlinesOf(rs), paragraph,
        ...(dualSpeech ? { dual: true } : {}),
        ...(line.sceneNumber !== undefined ? { sceneNumber: line.sceneNumber } : {}),
      });
    },
    invisible(line, text) {
      if (line.element === 'boneyard') return;
      paragraph++;
      const [loc, len] = trimmedRange(text, contentRange(line));
      let body = text.slice(loc, loc + len);
      if (line.element === 'note') body = body.replace(/^\[\[/, '').replace(/\]\]$/, '').trim();
      const inlines = line.element === 'note' ? plainInlines(body) : inlinesOf(runs(text, loc, len));
      out.push({ element: line.element as FountainElement, inlines, paragraph });
    },
  });
  return out;
}

/** `Fountain.speakers(in:)`: who speaks in a script, each cue's name in capitals without its extension or dual mark; a
 *  cue naming several ("MARA AND TOM", "MARA & TOM", "MARA/TOM") gives each. */
export function speakers(source: string): Set<string> {
  const out = new Set<string>();
  for (const line of classify(source)) {
    if (line.element !== 'character') continue;
    const [loc, len] = contentRange(line);
    for (const name of speakersOfCue(source.slice(loc, loc + len))) out.add(name);
  }
  return out;
}

/** `Fountain.speakers(ofCue:)`: the names one cue gives, in capitals ("MARA AND TOM ^" → MARA, TOM). */
export function speakersOfCue(cue: string): string[] {
  const name = cue.split('(')[0].replaceAll('^', '').toUpperCase();
  return name.replaceAll(' AND ', '/').replaceAll('&', '/').split('/').map((p) => p.replace(/^[ \t]+|[ \t]+$/g, '')).filter(Boolean);
}

export const fountain: FountainApi = { lines, plainText };
